<script setup lang="ts">
import {computed} from "vue";
import {CalendarSearch, Link, Sparkles} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from "@/core/components/ui/dialog";
import Customer from "@/customer/interfaces/Customer";

type Choice = 'link' | 'calendar' | 'journey';

const props = defineProps<{
    customer: Customer;
    /**
     * Whether the pharmacy can share a booking link at all: false while the
     * server does not know the public booking page, in which case the link
     * card is withheld rather than offered as a dead end.
     */
    bookingEnabled: boolean;
}>();

const emits = defineEmits<{
    (e: 'pick', choice: Choice): void;
}>();

const open = defineModel<boolean>('open', {required: true});

/**
 * The health journey is the choice the pharmacy is meant to push: it comes
 * first and is the only featured card.
 */
const CHOICES: {key: Choice; icon: typeof Link; title: string; description: string; cta: string; featured: boolean}[] = [
    {
        key: 'journey',
        icon: Sparkles,
        title: 'Automatiser le parcours officinal de santé',
        description: 'Une série de rendez-vous proposée selon le profil santé du patient : zona, grippe, bilan, entretien.',
        cta: 'Générer le parcours',
        featured: true
    },
    {
        key: 'calendar',
        icon: CalendarSearch,
        title: 'Choisir un créneau dans le calendrier',
        description: 'Réservez tout de suite un rendez-vous au comptoir : motif, collaborateur, date et heure.',
        cta: 'Ouvrir le calendrier',
        featured: false
    },
    {
        key: 'link',
        icon: Link,
        title: 'Envoyer un lien de rendez-vous',
        description: 'Le patient choisit lui-même son créneau depuis son mobile. Lien standard ou généré à la carte.',
        cta: 'Préparer le lien',
        featured: false
    }
];

const choices = computed(() => CHOICES.filter((choice) => choice.key !== 'link' || props.bookingEnabled));

const description = computed(() => props.bookingEnabled
    ? 'Trois façons de programmer la suite : le parcours officinal de santé d\'abord, ou un rendez-vous ponctuel.'
    : 'Deux façons de programmer la suite : le parcours officinal de santé d\'abord, ou un créneau précis dans le calendrier.');

/**
 * Close before handing over: the parent opens another dialog, and two of them
 * mounted at once would fight over the overlay and the focus trap.
 */
const pick = (choice: Choice) => {
    open.value = false;
    emits('pick', choice);
};
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="max-w-2xl">
      <DialogHeader>
        <DialogTitle>Prendre un rendez-vous — {{ customer.information.name }}</DialogTitle>
        <DialogDescription>
          {{ description }}
        </DialogDescription>
      </DialogHeader>
      <div class="flex flex-col gap-3">
        <button
          v-for="choice in choices"
          :key="choice.key"
          type="button"
          class="flex items-center gap-3.5 rounded-lg border-solid p-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          :class="choice.featured
            ? 'border-[1.5px] border-primary bg-primary/5 hover:bg-primary/10'
            : 'border border-gray-200 hover:border-primary hover:bg-primary-lightest'"
          @click="pick(choice.key)"
        >
          <span
            class="flex size-[42px] shrink-0 items-center justify-center rounded-xl"
            :class="choice.featured ? 'bg-primary text-white' : 'bg-primary-lightest text-primary'"
          >
            <component :is="choice.icon" class="size-5" />
          </span>
          <span class="flex min-w-0 flex-1 flex-col gap-0.5">
            <span class="text-sm font-semibold" :class="{'text-primary': choice.featured}">{{ choice.title }}</span>
            <span class="text-xs leading-relaxed" :class="choice.featured ? 'text-gray-600' : 'text-gray-500'">
              {{ choice.description }}
            </span>
          </span>
          <!--
            The whole card is the control; the button is its visible
            affordance, so it must not take the focus a second time.
          -->
          <Button
            v-if="choice.featured"
            size="xs"
            class="shrink-0 bg-primary text-white hover:bg-primary/90"
            tabindex="-1"
            as="span"
          >
            {{ choice.cta }}
          </Button>
          <Button v-else variant="outline" size="xs" class="shrink-0" tabindex="-1" as="span">
            {{ choice.cta }}
          </Button>
        </button>
      </div>
    </DialogContent>
  </Dialog>
</template>
